import React, { useMemo, useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Award, Copy, Check, ExternalLink } from 'lucide-react';

/**
 * "Embed in your README" badge snippet generator.  Renders a live preview of
 * the SVG badge plus three copy-paste snippets (Markdown, HTML, BBCode) so
 * partners and internal repos can advertise brand compliance with a single
 * line of code.
 */
export const BrandBadge = () => {
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
  const [copied, setCopied] = useState(null);
  const [linkTarget, setLinkTarget] = useState('guide');

  const linkUrls = {
    guide:    `${baseUrl}/`,
    md:       `${baseUrl}/jackstone-brand-guide.md`,
    llmstxt:  `${baseUrl}/llms.txt`,
  };
  const linkLabels = {
    guide:    'Live brand guide site',
    md:       'Complete brand-guide markdown',
    llmstxt:  'AI llms.txt discovery index',
  };
  const targetUrl = linkUrls[linkTarget];
  const badgeUrl = `${baseUrl}/badge.svg`;

  const snippets = useMemo(
    () => ({
      Markdown: `[![Jackstone Brand Compliant](${badgeUrl})](${targetUrl})`,
      HTML: `<a href="${targetUrl}"><img src="${badgeUrl}" alt="Jackstone Brand Compliant" height="26" /></a>`,
      BBCode: `[url=${targetUrl}][img]${badgeUrl}[/img][/url]`,
      'Direct image URL': badgeUrl,
    }),
    [badgeUrl, targetUrl],
  );

  const copy = async (label, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
      toast.success(`${label} copied`);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      toast.error('Clipboard unavailable — select and copy manually');
    }
  };

  return (
    <Card
      className="p-8 mt-6 border-jackstone-blue/30 bg-gradient-to-br from-background to-light-blue/30"
      data-testid="brand-badge-card"
    >
      <div className="flex items-start gap-4 mb-5">
        <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 bg-jackstone-blue/10">
          <Award className="h-6 w-6 text-jackstone-blue" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-1">
            <h3 className="text-2xl font-heading font-semibold text-jackstone-blue">
              Embed in your README
            </h3>
            <Badge tone="jackstone-blue" fill="soft">
              SVG
            </Badge>
          </div>
          <p className="text-sm text-subtle max-w-2xl">
            Drop the Jackstone Brand Compliant badge into any GitHub README,
            partner site, or internal portal so the world knows you're shipping
            on-brand. Pure SVG — sharp at every resolution, link-back included.
          </p>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-background p-5 mb-5 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="text-xs text-subtle">Preview:</span>
          <img src="/badge.svg" alt="Jackstone Brand Compliant badge" height="26" style={{ height: '26px', width: 'auto' }} data-testid="brand-badge-preview" />
        </div>
        <a
          href="/badge.svg"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-jackstone-blue hover:underline flex items-center gap-1"
          data-testid="brand-badge-view-svg"
        >
          View raw SVG
          <ExternalLink className="h-3 w-3" />
        </a>
      </div>

      <div className="mb-4">
        <p className="text-xs text-subtle mb-2 font-heading font-semibold uppercase tracking-wider">
          Badge links to
        </p>
        <div className="flex flex-wrap gap-2">
          {Object.entries(linkLabels).map(([key, label]) => (
            <Button
              key={key}
              type="button"
              size="sm"
              variant={linkTarget === key ? 'default' : 'outline'}
              onClick={() => setLinkTarget(key)}
              className="text-xs"
              data-testid={`brand-badge-target-${key}`}
            >
              {label}
            </Button>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        {Object.entries(snippets).map(([label, value]) => {
          const isCopied = copied === label;
          return (
            <div
              key={label}
              className="rounded-lg border border-border bg-background p-3"
              data-testid={`brand-badge-snippet-${label.toLowerCase().replace(/[^a-z]/g, '-')}`}
            >
              <div className="flex items-center justify-between mb-2">
                <Badge tone="grey" fill="soft" className="text-xs">{label}</Badge>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => copy(label, value)}
                  className="gap-2 text-xs h-7"
                  data-testid={`brand-badge-copy-${label.toLowerCase().replace(/[^a-z]/g, '-')}`}
                >
                  {isCopied ? (
                    <>
                      <Check className="h-3.5 w-3.5" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="h-3.5 w-3.5" />
                      Copy
                    </>
                  )}
                </Button>
              </div>
              <pre className="text-xs font-mono text-body overflow-x-auto whitespace-pre-wrap break-all">
                {value}
              </pre>
            </div>
          );
        })}
      </div>
    </Card>
  );
};
